import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Badge,
  Card,
  ConfirmModal,
  Disclosure,
  Icon,
  Modal,
  Page,
  SimpleSelect,
  SimpleSummary,
  Skeleton,
  SwitchInput,
  TextInput,
} from 'corso-admin-ui';
import { FormEventHandler, ReactNode, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DescriptionList from '~/components/DescriptionList';
import {
  useReturnLocationDelete,
  useReturnLocations,
  useReturnLocationUpsert,
} from '~/hooks/useReturnLocations';

import { CountryCode, geoCountryCodes, isEnum, Pegasus } from 'corso-types';
import Address from '~/components/Address';
import EmptyStateAction from '~/components/EmptyStateAction';

import { z } from 'zod';
import VerifyAddressButton from '~/components/VerifyAddressButton';
import { AutomationListByEventType } from '~/features/settings/automations/AutomationListByEventType';
import { useIntegrationSettingsData } from '~/hooks/useIntegrationSettings';
import {
  returnLocation,
  ReturnLocation,
  returnLocationCreate,
  ReturnLocationCreate,
  UpsertFormProps,
} from '~/types';
import { countryCodeOptions } from '~/utils/countryCodeOptions';

const prioritizedCountries: CountryCode[] = [CountryCode.US, CountryCode.CA];

const useSortedProvinceOptions = (countryCode?: string | null) =>
  useMemo(() => {
    const valid = isEnum(countryCode, CountryCode);

    if (!valid || !prioritizedCountries.includes(countryCode)) return [];

    return geoCountryCodes[countryCode].provinces
      .map((province) => ({
        label: province.name,
        value: province.code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countryCode]);

const requiredAddress = returnLocation.shape.address
  .omit({
    stateOrProvinceCode: true,
  })
  .extend({
    stateOrProvinceCode: z
      .string()
      .min(1, { message: 'State or province code is required' })
      .nullish(),
  });

const returnLocationUpdateSchema = returnLocation
  .omit({
    address: true,
  })
  .extend({
    address: requiredAddress,
  });

const returnLocationCreateSchema = returnLocationCreate
  .omit({
    address: true,
  })
  .extend({
    address: requiredAddress,
  });

function ReturnLocationForm({
  show: showForm,
  onSubmit,
  onClose: closeForm,
  values,
}: UpsertFormProps<
  z.infer<typeof returnLocationCreateSchema>,
  z.infer<typeof returnLocationUpdateSchema>,
  ReturnLocation
>) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(
      returnLocationUpdateSchema.or(returnLocationCreateSchema),
    ),
    values,
  });

  const countryCode = watch('address.countryCode');

  const provinceSelectOptions = useSortedProvinceOptions(countryCode);

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => {
      onSubmit(formValues);
      reset();
    })(event).catch(console.error);
  };

  return (
    <Modal
      title="Return Location"
      description="A location that represents a physical address where you can receive returns."
      show={showForm}
      onClose={closeForm}
    >
      <form className="flex flex-col gap-4" onSubmit={submitHandler}>
        <TextInput
          id="name"
          label="Internal Name"
          required
          {...register('name')}
          error={errors.name?.message}
          details="This is for internal use only and will not be visible to the customer."
        />

        <TextInput
          id="customs-signer-name"
          label="Customs Signer Name"
          {...register('customsSignerName')}
          error={errors.customsSignerName?.message}
          details="The name to use as the signer on customs forms, if applicable."
        />

        <Controller
          control={control}
          name="isQuickShipEnabled"
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <SwitchInput
              id="quick-ship"
              label="Quick Ship"
              details="If enabled, this Return Location will be used for generating Quick Shipments. Only one Return Location can be used for Quick Ship at a time."
              checked={value}
              onChange={onChange}
              error={errors.isQuickShipEnabled?.message}
            />
          )}
        />

        <hr className="my-2 border-t border-neutral-200" />
        <TextInput
          id="name"
          details="Used as the name on the return label and packing slip, if applicable."
          label="Shipping Name"
          autoComplete="address-name"
          required
          {...register('address.name')}
          error={errors.address?.line1?.message}
        />

        <Controller
          name="address.countryCode"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SimpleSelect
              label="Country"
              defaultValue="US"
              options={countryCodeOptions}
              value={value}
              onChange={(v) => {
                setValue('address.stateOrProvinceCode', '');
                onChange(v);
              }}
              error={errors.address?.countryCode?.message}
            />
          )}
        />

        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput
            id="address.line1"
            label="Address Line 1"
            autoComplete="address-line1"
            required
            {...register('address.line1')}
            error={errors.address?.line1?.message}
          />
          <TextInput
            id="address.line2"
            label="Address Line 2"
            autoComplete="address-line2"
            {...register('address.line2')}
            error={errors.address?.line2?.message}
          />
          <TextInput
            id="address.city"
            label="City"
            autoComplete="address-level2"
            required
            {...register('address.city')}
            error={errors.address?.city?.message}
            details="City or town name"
          />

          {provinceSelectOptions.length > 0 ? (
            <Controller
              name="address.stateOrProvinceCode"
              control={control}
              render={({ field: { onChange, value } }) => (
                <SimpleSelect
                  required
                  label="State or Province"
                  options={provinceSelectOptions}
                  value={value}
                  onChange={onChange}
                  error={errors.address?.stateOrProvinceCode?.message}
                />
              )}
            />
          ) : (
            <TextInput
              id="address.stateOrProvinceCode"
              label={
                provinceSelectOptions.length > 0
                  ? 'State or Province Code'
                  : 'State or Province'
              }
              autoComplete="address-level1"
              required
              {...register('address.stateOrProvinceCode')}
              error={errors.address?.stateOrProvinceCode?.message}
            />
          )}

          <TextInput
            id="address.postalCode"
            label="Postal Code"
            autoComplete="postal-code"
            details="Postal code or ZIP code"
            required={countryCode === CountryCode.US}
            {...register('address.postalCode')}
            error={errors.address?.postalCode?.message}
          />

          <TextInput
            id="phone"
            details="The phone number for the return location, required for some carriers."
            label="Phone"
            autoComplete="tel"
            {...register('address.phone')}
            error={errors.address?.phone?.message}
          />
        </div>

        <VerifyAddressButton
          address={watch('address')}
          onApplyCorrections={(corrected) => {
            if (corrected.line1) setValue('address.line1', corrected.line1);
            if (corrected.line2) setValue('address.line2', corrected.line2);
            if (corrected.city) setValue('address.city', corrected.city);
            if (corrected.postalCode)
              setValue('address.postalCode', corrected.postalCode);
            if (corrected.stateOrProvinceCode)
              setValue(
                'address.stateOrProvinceCode',
                corrected.stateOrProvinceCode,
              );
            if (corrected.countryCode)
              setValue('address.countryCode', corrected.countryCode);
          }}
        />

        <Action variant="primary" type="submit">
          {values ? 'Save' : 'Create'} Location
        </Action>
      </form>
    </Modal>
  );
}

function EditReturnLocation({
  location: { name, address, isQuickShipEnabled },
  actions,
}: {
  location: ReturnLocation;
  actions?: ReactNode;
}) {
  const descriptions = [
    {
      term: 'Quick Ship',
      details: isQuickShipEnabled ? 'Enabled' : 'Disabled',
    },
    {
      term: 'Address',
      details: (
        <>
          {address.name && <div>{address.name}</div>}
          <Address address={address} />
        </>
      ),
    },
  ];
  return (
    <Disclosure
      renderSummary={
        <SimpleSummary>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <h4 className="text-sm font-medium text-neutral-800">{name}</h4>
            {actions}
          </div>
        </SimpleSummary>
      }
    >
      <div className="px-2">
        <DescriptionList descriptions={descriptions} />
      </div>
    </Disclosure>
  );
}

function ReturnLocationSkeleton() {
  return (
    <div className="flex gap-2">
      <Skeleton.Rectangle height="36px" width="100%" />
      <Skeleton.Rectangle height="36px" width="44px" />
      <Skeleton.Rectangle height="36px" width="44px" />
    </div>
  );
}

function DeleteLocation({ location }: { location: ReturnLocation }) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutate: deleteLocation, isPending } = useReturnLocationDelete();

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const closeDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
  };

  const confirmDelete = () => {
    deleteLocation(location);
    closeDeleteConfirmation();
  };

  return (
    <>
      <Action
        icon="remove"
        variant="ghost"
        accessibilityLabel={`Delete ${location.name}`}
        onClick={handleDelete}
      />

      <ConfirmModal
        title="Delete Location"
        prompt={`Are you sure you want to delete ${location.name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        show={showDeleteConfirmation}
        loading={isPending}
        onConfirm={confirmDelete}
        onCancel={closeDeleteConfirmation}
      />
    </>
  );
}

function EditLocation({ location }: { location: ReturnLocation }) {
  const [showForm, setShowForm] = useState(false);
  const { mutate: upsertReturnLocation } = useReturnLocationUpsert();

  const editLocation = () => {
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
  };

  return (
    <>
      <Action
        icon="edit"
        variant="ghost"
        accessibilityLabel={`Edit ${location.name}`}
        onClick={editLocation}
      />
      <ReturnLocationForm
        show={showForm}
        values={location}
        onSubmit={(upsert) => {
          upsertReturnLocation(upsert);
          closeForm();
        }}
        onClose={closeForm}
      />
    </>
  );
}

function AddLocation({
  showForm,
  setShowForm,
}: {
  showForm: boolean;
  setShowForm: (show: boolean) => void;
}) {
  const { data: locations = [] } = useReturnLocations();
  const { mutate: saveLocation } = useReturnLocationUpsert();

  const openForm = () => setShowForm(true);
  const closeForm = () => setShowForm(false);

  const createLocation = (values: ReturnLocationCreate) => {
    saveLocation(values);
    closeForm();
  };

  return (
    <>
      {locations.length === 0 ? (
        <EmptyStateAction.Button
          onClick={openForm}
          icon={<Icon icon="box" />}
          label="Create a Return Location"
          description="Looks like you don't have any return locations yet. Create a return location, so you can configure Shipping Policies to accept returns."
        />
      ) : null}

      <ReturnLocationForm
        show={showForm}
        onSubmit={createLocation}
        onClose={closeForm}
      />
    </>
  );
}

function ReturnShippingConfigAutomationRules() {
  return (
    <Card>
      <AutomationListByEventType
        eventType={Pegasus.EventType.returnShippingConfig}
        hideIcon
        emptyText="No Return Shipping Policies, before you can accept returns you must create one."
      />
    </Card>
  );
}

export default function ShippingPolicySettingsPage() {
  const { data: returnLocations = [], isLoading } = useReturnLocations();
  const { data: { globalEConfig } = {} } = useIntegrationSettingsData();

  const [showForm, setShowForm] = useState(false);

  return (
    <Page title="Shipping Policies">
      <div className="flex flex-col gap-5">
        <Card>
          <Card.Line as="header">
            <Card.Heading as="h2">Return Locations</Card.Heading>
            <Action
              icon="add"
              onClick={() => setShowForm(true)}
              variant="ghost"
              accessibilityLabel="Add Location"
            />
          </Card.Line>
          <Card.Description>
            Locations represent physical addresses where you can receive
            returns, they are used in tandem with Shipping Policies to determine
            where to send returns.
          </Card.Description>

          <Skeleton
            isLoading={isLoading}
            instances={1}
            skeleton={ReturnLocationSkeleton}
          >
            <ul className="flex flex-col gap-2">
              {returnLocations.map((rl) => (
                <li key={rl.id}>
                  <EditReturnLocation
                    location={rl}
                    actions={
                      rl.isGlobalEManaged ? (
                        <Badge>Managed By Global-e</Badge>
                      ) : (
                        <div className="flex flex-row gap-2">
                          <EditLocation location={rl} />
                          <DeleteLocation location={rl} />
                        </div>
                      )
                    }
                  />
                </li>
              ))}
            </ul>
            <AddLocation showForm={showForm} setShowForm={setShowForm} />
          </Skeleton>
        </Card>

        {(!!returnLocations.length || !!globalEConfig) && (
          <ReturnShippingConfigAutomationRules />
        )}
      </div>
    </Page>
  );
}
